import xhr from 'xhr';
import {stateBackup} from './local';
import {STATUS_REQUEST, DATA_REQUEST, OUTCOMES_REQUEST} from './requestsQueue';

import config from 'config';

export function initTrackingLib({
  lang,
  campaignId,
  testId,
  userId,
  userData: {username, firstName, lastName, role, gender},
  assessedUsers,
  requestsQueue,
  logsQueue,
  onClose,
  onComplete,
  onUnauth,
}) {
  window.skillupAPI = {
    lang,
    campaignId: parseInt(campaignId, 10),
    testId: parseInt(testId, 10),
    userId: parseInt(userId, 10),
    userData: {
      username,
      firstName,
      lastName,
      role,
      gender,
    },
  };
  if (assessedUsers) {
    window.skillupAPI.assessedUsers = assessedUsers;
  }

  if (
    Number.isNaN(window.skillupAPI.campaignId) ||
    Number.isNaN(window.skillupAPI.testId) ||
    Number.isNaN(window.skillupAPI.userId)
  ) {
    throw new Error('Invalid tracking data');
  }

  // In memory values
  let initialized = false;
  let version = 0;
  let store = {};
  let status = 'not attempted';
  let outcomes = {};

  function checkInit() {
    if (!initialized) {
      throw new Error('Skillup API not initialized');
    }
  }

  function backupState() {
    const localUserState = stateBackup.get(userId) || {};
    stateBackup.set(
      userId,
      Object.assign(localUserState, {
        [campaignId]: {
          [testId]: {
            version,
            data: store,
            status,
            outcomes,
          },
        },
      })
    );
  }

  function clearTestBackup(testId) {
    const localUserBackup = stateBackup.get(userId);
    if (!localUserBackup) {
      return;
    }

    const localTestBackup = localUserBackup[campaignId][testId];
    if (!localTestBackup) {
      return;
    }

    delete localUserBackup[testId];

    stateBackup.set(userId, localUserBackup);
  }

  if (!window.skillupAPI.init) {
    window.skillupAPI.init = function init() {
      const promise = new Promise((resolve, reject) => {
        xhr(
          {
            method: 'get',
            url: `${config.api}/tests/${testId}/sync/${campaignId}/${userId}`,
            json: true,
            sync: false,
            timeout: config.requestTimeout,
          },
          (err, resp, body) => {
            if (err) {
              reject(new Error('Skillup API initialization error: ' + JSON.stringify(err)));
              return;
            }

            const {statusCode} = resp;
            if (statusCode >= 200 && statusCode < 400) {
              if (body.status === true) {
                resolve(body.data || {});
                return;
              }

              reject(new Error('Skillup API initialization error: ' + JSON.stringify(body)));
              return;
            }

            reject(new Error('Skillup API initialization error'));
          }
        );
      });

      return promise.then(data => {
        const localUserState = stateBackup.get(userId);
        const localTestState =
          localUserState && localUserState[campaignId] ? localUserState[campaignId][testId] : null;
        if (!localTestState || localTestState.version < data.version) {
          let remoteData;
          try {
            remoteData = JSON.parse(data.testData);
          } catch (e) {
            remoteData = {};
          }
          stateBackup.set(
            userId,
            Object.assign(localUserState || {}, {
              [campaignId]: {
                [testId]: {
                  version: data.version || 0,
                  status: data.testStatus || 'not attempted',
                  data: remoteData,
                },
              },
            })
          );
          version = data.version || 0;
          status = data.testStatus || 'not attempted';
          store = remoteData;
          initialized = true;

          return true;
        }

        version = localTestState.version || 0;
        status = localTestState.status || 'not attempted';
        let localData;
        try {
          localData =
            typeof localTestState.data === 'string'
              ? JSON.parse(localTestState.data)
              : localTestState.data;
        } catch (e) {
          localData = {};
        }
        store = localData;
        initialized = true;

        return true;
      });
    };
  }

  if (!window.skillupAPI.trackOutcomes) {
    window.skillupAPI.trackOutcomes = function trackOutcomes(values) {
      checkInit();

      outcomes = values;
      backupState();

      if (onComplete) {
        onComplete({campaignId, testId, userId});
      }

      const request = {
        campaignId,
        testId,
        userId,
        type: OUTCOMES_REQUEST,
        version: 0,
        data: values,
        callback: () => clearTestBackup(testId),
      };
      requestsQueue.push(request);

      return {
        status: true,
      };
    };

    window.skillupAPI.trackOutcomesAsync = function trackOutcomesAsync(values) {
      return Promise.resolve(window.skillupAPI.trackOutcomes(values));
    };
  }

  if (!window.skillupAPI.getData) {
    window.skillupAPI.getData = function getData() {
      checkInit();

      return store || {};
    };

    window.skillupAPI.getDataAsync = function getDataAsync() {
      return Promise.resolve(window.skillupAPI.getData());
    };
  }

  if (!window.skillupAPI.setData) {
    window.skillupAPI.setData = function setData(data, user = userId) {
      checkInit();

      version = Number.isInteger(version) ? version + 1 : 0;
      store = data;
      backupState();

      const request = {
        campaignId,
        testId,
        userId: user,
        type: DATA_REQUEST,
        version,
        data: JSON.stringify(store),
      };
      requestsQueue.push(request);

      return store;
    };

    window.skillupAPI.setDataAsync = function setDataAsync(jsonString, user) {
      return Promise.resolve(window.skillupAPI.setData(jsonString, user));
    };
  }

  if (!window.skillupAPI.getStatus) {
    window.skillupAPI.getStatus = function getStatus() {
      checkInit();

      return status || 'not attempted';
    };

    window.skillupAPI.getStatusAsync = function getStatusAsync() {
      return Promise.resolve(window.skillupAPI.getStatus());
    };
  }

  if (!window.skillupAPI.setStatus) {
    window.skillupAPI.setStatus = function setStatus(value) {
      checkInit();

      version += 1;
      status = value;
      backupState();

      const request = {
        campaignId,
        testId,
        userId,
        type: STATUS_REQUEST,
        version,
        status,
      };
      requestsQueue.push(request);

      if (value === 'complete' && onComplete) {
        onComplete({campaignId, testId, userId});
      }

      return status;
    };

    window.skillupAPI.setStatusAsync = function setStatus(statusVal) {
      return Promise.resolve(window.skillupAPI.setStatus(statusVal));
    };
  }

  if (!window.skillupAPI.markAsComplete) {
    window.skillupAPI.markAsComplete = function markAsComplete() {
      return window.skillupAPI.setStatus('complete');
    };

    window.skillupAPI.markAsCompleteAsync = function markAsCompleteAsync() {
      return window.skillupAPI.setStatusAsync('complete');
    };
  }

  if (!window.skillupAPI.getAssessedOutcomes) {
    window.skillupAPI.getAssessedOutcomes = function getAssessedOutcomes() {
      return new Promise((resolve, reject) => {
        xhr(
          {
            method: 'get',
            url: `${config.api}/tests/${testId}/assessedOutcomes/${campaignId}`,
            json: true,
            sync: false,
            timeout: config.requestTimeout,
          },
          (err, resp, body) => {
            if (err) {
              reject(new Error('Assessed outcomes retrieval error: ' + JSON.stringify(err)));
              return;
            }

            const {statusCode} = resp;
            if (statusCode >= 200 && statusCode < 400) {
              if (body.status === true) {
                resolve(body.list || {});
                return;
              }

              reject(new Error('Assessed outcomes retrieval error: ' + JSON.stringify(body)));
              return;
            }

            reject(new Error('Assessed outcomes retrieval error'));
          }
        );
      });
    };

    window.skillupAPI.getAssessedOutcomesAsync = window.skillupAPI.getAssessedOutcomes;
  }

  function stringify(data) {
    return typeof data === 'string' ? data : JSON.stringify(data);
  }

  if (!window.skillupAPI.trackAssessedOutcomes) {
    window.skillupAPI.trackAssessedOutcomes = function trackOutcomes(assessedId, values) {
      return new Promise((resolve, reject) => {
        xhr(
          {
            method: 'post',
            url: `${config.api}/outcomes/${campaignId}/${testId}/${assessedId}`,
            data: {values},
            json: true,
            sync: false,
            timeout: config.requestTimeout,
          },
          (err, resp, body) => {
            if (err) {
              reject(new Error('Assessed tracking error: ' + JSON.stringify(err)));
              return;
            }

            const {statusCode} = resp;
            if (statusCode >= 200 && statusCode < 400) {
              if (body.status === true) {
                resolve(true);
                return;
              }

              reject(new Error('Assessed tracking error: ' + JSON.stringify(body)));
              return;
            }

            reject(new Error('Assessed tracking error'));
          }
        );
      });
    };

    window.skillupAPI.trackAssessedOutcomesAsync = window.skillupAPI.trackAssessedOutcomes;
  }

  if (!window.skillupAPI.getAssessedStore) {
    window.skillupAPI.getAssessedStore = function (user) {
      return new Promise((resolve, reject) => {
        if (!user) {
          reject('no user id defined');
          return;
        }

        xhr(
          {
            method: 'get',
            url: `${config.api}/tests/${testId}/data/${campaignId}/${user}`,
            json: true,
            sync: false,
            timeout: config.requestTimeout,
          },
          (err, res, body) => {
            if (err) {
              reject(new Error('Assessed store error: ' + JSON.stringify(err)));
              return;
            }

            const {statusCode} = res;

            if (statusCode >= 200 && statusCode < 400) {
              if (body.status === true) {
                resolve(body.data || {});
                return;
              }

              reject(new Error('Assessed store retrieval error: ' + JSON.stringify(body)));
              return;
            }

            reject(new Error('Assessed store retrieval error'));
          }
        );
      });
    };
  }

  if (!window.skillupAPI.close) {
    window.skillupAPI.close = function close() {
      if (onClose) {
        onClose();
      }
    };
  }

  if (!window.skillupAPI.callRPCWith) {
    window.skillupAPI.callRPCWith = function callRPCWith({action, data}) {
      return new Promise((resolve, reject) => {
        if (!action) {
          reject('action not provided');
        }

        xhr(
          {
            method: 'post',
            url: `${config.api}/v2/rpc/${campaignId}/${testId}`,
            data: {action, data},
            json: true,
            sync: false,
            timeout: config.requestTimeout,
          },
          (err, resp, body) => {
            if (err) {
              reject({xhrErr: err});
              return;
            }

            const {statusCode} = resp;

            if (statusCode === 401 || statusCode === 403) {
              onUnauth();
              reject(null);
              return;
            }

            //SERVER'S BUSY
            if (statusCode === 503 || statusCode === 502) {
              reject({busy: true});
              return;
            }

            if (statusCode >= 200 && statusCode < 400) {
              resolve(body);
              return;
            }

            reject(body);
          }
        );
      });
    };
  }

  if (!window.skillupAPI.sendLog) {
    window.skillupAPI.sendLog = function sendLog(log, highPriority = false) {
      highPriority ? logsQueue.unshift(log) : logsQueue.push(log);
    };
  }
}
